<script setup lang="ts">
  import type { Task } from '@api/models';
  import { storeToRefs } from 'pinia';
  import { computed, useTemplateRef } from 'vue';

  import { Button } from '@ui/components/button';

  import { useTaskCollectionStore } from '@/store';

  interface Props {
    task?: Task;
  }

  const props = defineProps<Props>();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const componentTask = computed(() => props.task ?? currentTask.value);
  const afterCompleteOverlayRef = useTemplateRef('afterCompleteOverlayRef');

  defineExpose({
    el: afterCompleteOverlayRef,
  });
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-300 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-300 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="taskCollectionStore?.showAfterCompleteOverlay"
      ref="afterCompleteOverlayRef"
      class="absolute top-0 left-0 right-0 bottom-0 z-50 bg-overlay/80 rounded-sm flex items-center justify-center"
    >
      <Button
        class="absolute top-4 right-4"
        size="icon"
        @click="taskCollectionStore?.closeAfterCompleteOverlay"
      >
        <i class="fa fas fa-close" />
      </Button>
      <div class="flex-row items-center space-y-8">
        <div class="flex items-center justify-center gap-x-2.5">
          <i class="fas fa-check-circle text-success text-xl size-5" />

          <h3 class="text-xl text-white">Task {{ componentTask?.status }}!</h3>
        </div>

        <div class="flex items-center gap-x-5">
          <RouterLink
            v-if="componentTask?.isAnyDispute && componentTask?.dispute_id"
            :to="{
              name: 'disputes.dispute',
              params: { id: componentTask?.dispute_id },
            }"
          >
            <Button variant="primary" size="sm"> View Dispute </Button>
          </RouterLink>

          <Button
            v-else
            variant="outline"
            size="sm"
            @click="taskCollectionStore?.closeAfterCompleteOverlay"
          >
            Stay here
          </Button>

          <Button size="sm" @click="taskCollectionStore?.handleConfirmAfterComplete">
            <span>Next task</span>
            <i class="far fa-arrow-right" />
          </Button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped></style>
